import React, { useState } from "react";
import {
  TextField,
  Button,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import "../App.css";
import { db } from "../firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";

const ContactUs = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleCloseSnackbar = () =>
    setSnackbar((s) => ({ ...s, open: false }));

  const validateField = (name, value) => {
    let error = "";

    if (!value?.trim()) {
      if (name === "subject") {
        error = "Subject is required.";
      } else if (name === "message") {
        error = "Message cannot be empty.";
      } else {
        error = "This field is required.";
      }
    } else if (name === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value.trim())) {
        error = "Please enter a valid email address.";
      }
    }

    setErrors((prev) => ({ ...prev, [name]: error }));
    return error;
  };

  const validateAll = () => {
    const newErrors = {};
    Object.entries(formData).forEach(([key, value]) => {
      const error = validateField(key, value);
      if (error) {
        newErrors[key] = error;
      }
    });
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // live validate as user types
    validateField(name, value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateAll();
    if (Object.values(validationErrors).some((msg) => msg)) {
      setSnackbar({
        open: true,
        message: "Please fix the highlighted fields.",
        severity: "warning",
      });
      return;
    }

    setSubmitting(true);
    try {
      await addDoc(collection(db, "contactMessages"), {
        name: formData.name.trim(),
        email: formData.email.trim(),
        subject: formData.subject.trim(),
        message: formData.message.trim(),
        resolved: false,
        timestamp: serverTimestamp(),
      });

      setSnackbar({
        open: true,
        message: "✅ Your message has been sent!",
        severity: "success",
      });

      setFormData({ name: "", email: "", subject: "", message: "" });
      setErrors({});
    } catch (error) {
      console.error("❌ Error sending message:", error);
      setSnackbar({
        open: true,
        message: "Something went wrong. Please try again.",
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        backgroundImage: `url(${require("../assets/images/landing_background.jpg")})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        fontFamily: "Montserrat, sans-serif",
        justifyContent: "space-between",
      }}
    >
      {/* Header – matched to LandingPage style */}
      <header
        style={{
          width: "100%",
          padding: "10px 20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.5)",
          overflowX: "auto",
          whiteSpace: "nowrap",
        }}
      >
        <img
          src={require("../assets/images/logo_name.PNG")}
          alt="YourStoryWorld Logo"
          style={{
            height: "40px",
            cursor: "pointer",
            flexShrink: 0,
          }}
          onClick={() => navigate("/")}
        />
        <div
          style={{
            display: "inline-flex",
            gap: "15px",
            justifyContent: "flex-end",
            paddingRight: "10px",
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          <Button
            color="inherit"
            onClick={() => navigate("/")}
            style={headerButtonStyle}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = "#1976D2";
              e.currentTarget.style.color = "white";
              e.currentTarget.style.border = "1px solid white";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.color = "white";
              e.currentTarget.style.border = "1px solid transparent";
            }}
          >
            Home
          </Button>
          <Button
            color="inherit"
            onClick={() => navigate("/login")}
            style={headerButtonStyle}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = "#1976D2";
              e.currentTarget.style.color = "white";
              e.currentTarget.style.border = "1px solid white";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.color = "white";
              e.currentTarget.style.border = "1px solid transparent";
            }}
          >
            Sign In
          </Button>
          <Button
            color="inherit"
            onClick={() => navigate("/help")}
            style={headerButtonStyle}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = "#D32F2F";
              e.currentTarget.style.color = "white";
              e.currentTarget.style.border = "1px solid white";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.color = "white";
              e.currentTarget.style.border = "1px solid transparent";
            }}
          >
            Help
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main
        style={{
          background: "rgba(255, 255, 255, 0.95)",
          padding: "40px",
          borderRadius: "10px",
          maxWidth: "800px",
          margin: "20px auto",
          color: "#333",
        }}
      >
        <Typography variant="h4" gutterBottom color="primary">
          📬 Contact Us
        </Typography>
        <Typography variant="body1" paragraph>
          Have a question, suggestion, or issue? Fill out the form below and we’ll
          get back to you shortly.
        </Typography>
        <Typography
          variant="body2"
          paragraph
          style={{ opacity: 0.8, marginBottom: 24 }}
        >
          You can also email us directly at{" "}
          <a href="mailto:support@yourstoryworld.com">
            support@yourstoryworld.com
          </a>
          .
        </Typography>

        <form
          onSubmit={handleSubmit}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "20px",
          }}
        >
          <TextField
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            error={!!errors.name}
            helperText={errors.name || ""}
            fullWidth
          />
          <TextField
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
            error={!!errors.email}
            helperText={errors.email || ""}
            fullWidth
          />
          <TextField
            label="Subject"
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            required
            error={!!errors.subject}
            helperText={errors.subject || ""}
            fullWidth
          />
          <TextField
            label="Message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            multiline
            rows={4}
            required
            error={!!errors.message}
            helperText={errors.message || ""}
            fullWidth
          />

          <Button
            variant="contained"
            type="submit"
            disabled={submitting}
            sx={{
              backgroundColor: "#007BFF",
              color: "white",
              fontWeight: "bold",
              paddingY: 1.2,
            }}
          >
            {submitting ? (
              <>
                <CircularProgress size={20} sx={{ marginRight: 1 }} />
                Sending...
              </>
            ) : (
              "Send Message"
            )}
          </Button>
        </form>
      </main>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Footer */}
      <footer
        style={{
          textAlign: "center",
          padding: "20px",
          backgroundColor: "#000",
          color: "#fff",
        }}
      >
        <Typography>
          &copy; {new Date().getFullYear()} YourStoryWorld | All Rights Reserved
        </Typography>
        <Typography variant="body2">
          <a href="/about" style={{ color: "white", textDecoration: "none" }}>
            About Us
          </a>{" "}
          |{" "}
          <a href="/contact" style={{ color: "white", textDecoration: "none" }}>
            Contact
          </a>{" "}
          |{" "}
          <a
            href="/feedback"
            style={{ color: "white", textDecoration: "none" }}
          >
            Feedback
          </a>
        </Typography>
      </footer>
    </div>
  );
};

const headerButtonStyle = {
  fontSize: "16px",
  fontWeight: "bold",
  textTransform: "uppercase",
  color: "white",
  border: "1px solid transparent",
  transition: "all 0.3s ease",
};

export default ContactUs;

